import { useMutation, useQuery } from '@tanstack/react-query';
import { Edit2, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { customerAPI, licenceAPI } from '../api/index';
import { queryClient } from '../App';
import { Button } from '../components/Button';
import { ExpiryBadge } from '../components/ExpiryBadge';
import AddLicenceModal from '../components/modals/AddLicenceModal';
import { PageHeader } from '../components/PageHeader';
import { daysRemaining, formatDate } from '../lib/utils';
import type { Licence } from '../types/index';

export function LicencesPage() {
  const navigate = useNavigate()
  const [status, setStatus] = useState<'all' | 'active' | 'expiring_soon' | 'expired'>('all')
  const [offset, setOffset] = useState(0)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [showCustomerPicker, setShowCustomerPicker] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [selectedCustomerId, setSelectedCustomerId] = useState('')
  const [selectedLicence, setSelectedLicence] = useState<Licence | undefined>()
  const [customerSearch, setCustomerSearch] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['licences', status, offset],
    queryFn: () =>
      licenceAPI.list({
        status: status === 'all' ? undefined : status,
        limit: 20,
        offset,
      }).then(r => r.data),
  })

  const { data: customersData, isLoading: customersLoading } = useQuery({
    queryKey: ['customers', 'licence-picker'],
    queryFn: () =>
      customerAPI.list({
        limit: 200,
        offset: 0,
        active: true,
      }).then((r) => r.data),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => licenceAPI.delete(id),
    onSuccess: () => {
      toast.success('Licence deleted')
      queryClient.invalidateQueries({ queryKey: ['licences'] })
      setDeleteId(null)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error ?? 'Failed to delete')
    },
  })

  const total = data?.total || 0
  const licences = data?.licences || []
  const totalPages = Math.ceil(total / 20)
  const currentPage = Math.floor(offset / 20) + 1

  const tabs = [
    { id: 'all' as const, label: 'All' },
    { id: 'active' as const, label: 'Active' },
    { id: 'expiring_soon' as const, label: 'Expiring Soon' },
    { id: 'expired' as const, label: 'Expired' },
  ]

  const customers = customersData?.customers || []

  const filteredCustomers = customers.filter((customer) =>
    `${customer.full_name} ${customer.phone}`.toLowerCase().includes(customerSearch.toLowerCase())
  )

  return (
    <>
      <PageHeader title="Licences" action={{ label: 'Add Licence', onClick: () => setShowCustomerPicker(true) }} />

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => { setStatus(tab.id); setOffset(0) }}
            className={`px-4 py-3 font-medium border-b-2 transition-colors ${
              status === tab.id
                ? 'border-saffron text-saffron'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="space-y-2 p-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        ) : licences.length === 0 ? (
          <div className="flex h-48 items-center justify-center text-gray-500">
            <div className="text-center">
              <p className="mb-4">No licences found</p>
              <Button onClick={() => setShowCustomerPicker(true)} className="bg-saffron">
                <Plus className="w-4 h-4 mr-2" />
                Create Licence
              </Button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-4">Customer</th>
                  <th className="text-left py-3 px-4">Licence #</th>
                  <th className="text-left py-3 px-4">Type</th>
                  <th className="text-left py-3 px-4">Classes</th>
                  <th className="text-left py-3 px-4">Expiry</th>
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-right py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {licences.map((licence: any) => (
                  <tr key={licence.id} className="hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <button onClick={() => navigate(`/customers/${licence.customer_id}`)} className="text-saffron hover:underline">
                        {licence.customer_name}
                      </button>
                    </td>
                    <td className="py-3 px-4 font-medium">{licence.licence_number}</td>
                    <td className="py-3 px-4"><span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">{licence.licence_type}</span></td>
                    <td className="py-3 px-4 text-xs">{licence.vehicle_classes?.join(', ') || '-'}</td>
                    <td className="py-3 px-4">{formatDate(licence.expiry_date)}</td>
                    <td className="py-3 px-4"><ExpiryBadge days={daysRemaining(licence.expiry_date)} /></td>
                    <td className="py-3 px-4 text-right flex justify-end gap-2">
                      <Button size="sm" variant="secondary" onClick={() => {
                        setSelectedLicence(licence);
                        setSelectedCustomerId(licence.customer_id);
                        setShowAddModal(true);
                      }}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => setDeleteId(licence.id)}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!isLoading && licences.length > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-600">Showing {Math.min(offset + 20, total)} of {total}</p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - 20))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() => setOffset(offset + 20)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm">
            <p className="text-gray-900 font-medium mb-4">Delete this licence?</p>
            <div className="flex gap-3">
              <Button variant="secondary" onClick={() => setDeleteId(null)}>Cancel</Button>
              <Button
                className="bg-red-600 hover:bg-red-700"
                onClick={() => deleteMutation.mutate(deleteId)}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {showCustomerPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Customer</h3>
            <input
              type="text"
              placeholder="Search by name or phone..."
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-4 py-2 mb-3 focus:border-saffron focus:outline-none"
              disabled={customersLoading}
              autoFocus
            />
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
              {filteredCustomers.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  {customerSearch ? 'No customers found' : 'No customers available'}
                </div>
              ) : (
                filteredCustomers.map((customer) => (
                  <button
                    key={customer.id}
                    onClick={() => {
                      setSelectedCustomerId(customer.id)
                      setShowCustomerPicker(false)
                      setShowAddModal(true)
                    }}
                    className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-saffron/5 transition-colors ${
                      selectedCustomerId === customer.id ? 'bg-saffron/10' : ''
                    }`}
                  >
                    <div className="font-medium text-gray-900">{customer.full_name}</div>
                    <div className="text-sm text-gray-500">{customer.phone}</div>
                  </button>
                ))
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button 
                variant="secondary" 
                onClick={() => { 
                  setShowCustomerPicker(false)
                  setSelectedCustomerId('')
                  setCustomerSearch('')
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      <AddLicenceModal
        open={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setSelectedLicence(undefined);
        }}
        customerId={selectedCustomerId}
        licence={selectedLicence}
        onSuccess={() => {
          setShowAddModal(false);
          setSelectedLicence(undefined);
          queryClient.invalidateQueries({ queryKey: ['licences'] });
        }}
      />
    </>
  )
}
